// src/components/client/HouseholdProfile.js
import React, { useEffect, useState, useMemo } from "react";
import {
  Typography, Container, TextField, FormControl, Select, MenuItem, InputLabel,
  Checkbox, Button, Grid, Box, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Card, CardContent, CardHeader, useTheme, Chip,
  ListItemText, InputAdornment, Tooltip
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { backendLangFromI18n } from "../../utils/lang";
import api from "../../services/api";
import { useTranslation } from "react-i18next";

import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import EuroRoundedIcon from "@mui/icons-material/EuroRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import DinnerDiningRoundedIcon from "@mui/icons-material/DinnerDiningRounded";
import SpaRoundedIcon from "@mui/icons-material/SpaRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FlagCircleRoundedIcon from "@mui/icons-material/FlagCircleRounded";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useLocationCtx } from "../../context/LocationContext";
import countries from "../../countries.json";
import LocationGate from "../LocationGate";

const { household, users, guest, shoppingList } = api;

async function ensureGuestSession() {
  try {
    await guest.start(); // χτυπάει /api/users/guest/start
    return true;
  } catch (err) {
    console.error("guest.start failed", err);
    return false;
  }
}
const HouseholdProfile = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "gr" ? "el" : i18n.language || "en";
  const backendLang = backendLangFromI18n(i18n.language);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // ---- Location context (κρατάμε μόνο χώρα από το context)
  const { country: locCountry } = useLocationCtx();
  const countryObj = countries.find((c) => String(c.id) === String(locCountry));
  const isUSCtx = String(locCountry) === "2";

  // ---- Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authLoaded, setAuthLoaded] = useState(false);

  // ---- Profile state
  const [profile, setProfile] = useState({
    household_members: "",
    batch_servings: "",
    cooking_servings: "",
    cooking_mode: "exact",
    leftover_fridge_days: "",
    leftover_freezer_days: "",
    storage_mode: "",
    cooking_frequency: "",
    max_fridge_days_override: "",
    max_freezer_days_override: "",
    max_leftover_meals_per_week: "",
    diet_type_ids: [],
    country_recipes_ids: [],
    location_country_id: "",
    religious_preference_id: null,
    money_available: "",
    meal_options: [],
    food_types: [],
    // US extras (θα τα γεμίσουμε με state/city/zip)
    us_state: "",
    us_zip: "",
    store_location_id: "", // deprecated
  });

  // ---- US geo (state → city → zip) από το root /us_supported_locations
  const [usLocations, setUsLocations] = useState([]); // [{state, cities:[{city,zips:[]}, ...]}, ...]
  const [loadingUsLocations, setLoadingUsLocations] = useState(false);
  const [selectedUsState, setSelectedUsState] = useState("");
  const [selectedUsCity, setSelectedUsCity] = useState("");

  // ---- Helpers
  const isUSForForm = String(profile.location_country_id || locCountry) === "2";
  const [showLocationGate, setShowLocationGate] = useState(false);

  const [dietTypes, setDietTypes] = useState([]);
  const [countryRecipes, setCountryRecipes] = useState([]);
  const [religiousPreferences, setReligiousPreferences] = useState([]);
  const [foodTypes, setFoodTypes] = useState([]);
  const [mealOptions, setMealOptions] = useState([]);

  const [dietTypeSelectOpen, setDietTypeSelectOpen] = useState(false);
  const [countrySelectOpen, setCountrySelectOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const isValidZip = (z) => /^\d{5}$/.test(String(z || "").trim());

  // ---- city / zips derivations για ΗΠΑ

  const usStateOptions = useMemo(
    () => usLocations.map((s) => s.state),
    [usLocations]
  );

  const usCityOptions = useMemo(() => {
    if (!selectedUsState) return [];
    const stateObj = usLocations.find((s) => s.state === selectedUsState);
    if (!stateObj || !Array.isArray(stateObj.cities)) return [];
    return stateObj.cities.map((c) => c.city);
  }, [usLocations, selectedUsState]);

  const zipsForSelection = useMemo(() => {
    if (!selectedUsState) return [];
    const stateObj = usLocations.find((s) => s.state === selectedUsState);
    if (!stateObj || !Array.isArray(stateObj.cities)) return [];
    if (!selectedUsCity) {
      // Αν δεν έχει επιλεγεί πόλη, δώσε όλα τα ZIP της πολιτείας
      return stateObj.cities.flatMap((c) => c.zips || []);
    }
    const cityObj = stateObj.cities.find((c) => c.city === selectedUsCity);
    if (!cityObj || !Array.isArray(cityObj.zips)) return [];
    return cityObj.zips;
  }, [usLocations, selectedUsState, selectedUsCity]);

  const visibleZips = useMemo(
    () => zipsForSelection.slice(0, 20),
    [zipsForSelection]
  );

  const zipSupported = useMemo(() => {
    if (!isUSForForm) return true;
    const z = (profile.us_zip || "").trim();
    if (!z) return false;
    if (!isValidZip(z)) return false;

    if (zipsForSelection.length > 0) {
      return zipsForSelection.includes(z);
    }
    // Fallback: έλεγχος σε όλα τα ZIPs από τα usLocations
    const allZips = usLocations
      .flatMap((s) => s.cities || [])
      .flatMap((c) => c.zips || []);
    return allZips.includes(z);
  }, [isUSForForm, profile.us_zip, zipsForSelection, usLocations]);

  // Συγχρονισμός φόρτωσης/αρχικοποίησης
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setIsLoading(true);
        setError("");
        const dictParams = { params: { lang: backendLang } };

        // 0) Auth
        let userRes = null;
        try {
          userRes = await users.getCurrentUser();
        } catch {
          userRes = null;
        }
        const u = userRes?.data || null;
        const logged =
          Boolean(u?.audience?.is_authenticated) ||
          Boolean(u?.is_authenticated);
        setIsLoggedIn(logged);
        setAuthLoaded(true);

        // 1) Profile (backend χειρίζεται user/guest)
        let dataFromApi = null;
        try {
          const pr = await household.getProfile();
          dataFromApi = pr?.data?.data || null;
        } catch {
          dataFromApi = null;
        }

        // Αν είμαστε guest και δεν υπάρχει profile, δοκίμασε guest state
        if (!dataFromApi && !logged) {
          try {
            const gs = await guest.getState();
            const guestData = gs?.data?.household_profile || null;
            if (guestData) dataFromApi = guestData;
          } catch {
            // ignore
          }
        }

        if (dataFromApi) {
          setProfile({
            id: dataFromApi.id,
            household_members: dataFromApi.household_members ?? "",
            batch_servings: dataFromApi.batch_servings ?? "",
            cooking_servings:
              dataFromApi.cooking_servings ?? dataFromApi.batch_servings ?? "",
            cooking_mode: dataFromApi.cooking_mode ?? "exact",
            leftover_fridge_days: dataFromApi.leftover_fridge_days ?? "",
            leftover_freezer_days: dataFromApi.leftover_freezer_days ?? "",
            storage_mode: dataFromApi.storage_mode ?? "",
            cooking_frequency: dataFromApi.cooking_frequency ?? "",
            max_fridge_days_override: dataFromApi.max_fridge_days_override ?? "",
            max_freezer_days_override: dataFromApi.max_freezer_days_override ?? "",
            max_leftover_meals_per_week: dataFromApi.max_leftover_meals_per_week ?? "",
            diet_type_ids: dataFromApi.diet_type_ids ?? [],
            country_recipes_ids: dataFromApi.country_recipes_ids ?? [],
            location_country_id: dataFromApi.location_country_id ?? "",
            religious_preference_id: dataFromApi.religious_preference_id ?? null,
            money_available: dataFromApi.money_available ?? "",
            meal_options: dataFromApi.meal_options ?? [],
            food_types: dataFromApi.food_types ?? [],
            us_state: dataFromApi.us_state ?? "",
            us_zip: dataFromApi.us_zip ?? "",
            store_location_id: dataFromApi.store_location_id ?? "",
          });
        } else {
          // defaults
          if (locCountry && !isNaN(Number(locCountry))) {
            setProfile((p) => ({ ...p, location_country_id: Number(locCountry) }));
          }
        }

        // 2) Dictionaries
        // 2) Dictionaries
        const [dietRes, countryRes, religiousRes, foodRes, mealRes] =
          await Promise.all([
            household.getDietTypes(dictParams),
            household.getCountryRecipes(dictParams),
            household.getReligiousPreferences(dictParams),
            household.getFoodTypes(dictParams),
            household.getMealTypes(dictParams),
          ]);

        setDietTypes(dietRes.data || []);
        setCountryRecipes(countryRes.data || []);
        setFoodTypes(foodRes.data || []);
        setMealOptions(mealRes.data || []);

        // ✅ religious: νέο format { items, default_id }
        const religiousPayload = religiousRes.data || {};
        const religiousItems = religiousPayload.items || [];
        const religiousDefaultId = religiousPayload.default_id ?? null;

        setReligiousPreferences(religiousItems);

        // ✅ Αν δεν υπάρχει ήδη τιμή στο profile, βάλε default
        setProfile((prev) => {
          const hasValue =
            prev.religious_preference_id !== "" &&
            prev.religious_preference_id !== null &&
            prev.religious_preference_id !== undefined;

          if (hasValue) return prev;
          if (!religiousDefaultId) return prev;

          return { ...prev, religious_preference_id: religiousDefaultId };
        });

        setDietTypes(dietRes.data || []);
        setCountryRecipes(countryRes.data || []);
        setFoodTypes(foodRes.data || []);
        setMealOptions(mealRes.data || []);
      } catch (err) {
        setError(
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load household profile."
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendLang]);

  // 🔹 Φέρνουμε US supported locations (state → cities → zips) όταν το προφίλ είναι για ΗΠΑ
  useEffect(() => {
    if (!isUSForForm) return;

    setLoadingUsLocations(true);
    shoppingList
      .getUSSupportedLocations()
      .then((res) => {
        const list = res?.data?.states || [];
        setUsLocations(list);
      })
      .catch((err) => {
        console.error("Failed to load us_supported_locations:", err);
        setUsLocations([]);
      })
      .finally(() => {
        setLoadingUsLocations(false);
      });
  }, [isUSForForm]);

  // 🔹 Αρχικοποίηση selectedUsState / selectedUsCity από profile ή defaults
  useEffect(() => {
    if (!isUSForForm || !usLocations.length || selectedUsState) return;

    let stateToUse = profile.us_state;
    if (!stateToUse) {
      const hasCA = usLocations.find((s) => s.state === "CA");
      stateToUse = hasCA ? "CA" : usLocations[0].state;
    }
    const stateObj =
      usLocations.find((s) => s.state === stateToUse) || usLocations[0];

    let cityToUse = "";
    // Προσπάθησε να βρεις πόλη από το us_zip
    if (profile.us_zip && stateObj && stateObj.cities) {
      for (const c of stateObj.cities) {
        if ((c.zips || []).includes(profile.us_zip)) {
          cityToUse = c.city;
          break;
        }
      }
    }
    if (!cityToUse && stateObj && stateObj.cities && stateObj.cities.length) {
      cityToUse = stateObj.cities[0].city;
    }

    setSelectedUsState(stateObj.state);
    setSelectedUsCity(cityToUse);

    // Σιγουρέψου ότι έχουμε τουλάχιστον το state στο profile
    setProfile((prev) => {
      if (prev.us_state === stateObj.state) return prev;
      return { ...prev, us_state: stateObj.state };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUSForForm, usLocations, selectedUsState, profile.us_state, profile.us_zip]);

  // Όταν αλλάζει χώρα, καθάρισε US fields αν δεν είναι ΗΠΑ
  const handleInputChange = (field, value) => {
    setProfile((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "location_country_id") {
        const isUS = String(value) === "2";
        if (!isUS) {
          next.us_zip = "";
          next.us_state = "";
        }
      }
      return next;
    });
  };

  const handleFoodTypeCheckboxChange = (id) => {
    const current = profile.food_types || [];
    const updated = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    handleInputChange("food_types", updated);
  };

  const handleMealOptionChange = (id) => {
    const current = profile.meal_options || [];
    const updated = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    handleInputChange("meal_options", updated);
  };

  // 🔹 State change handler (ΗΠΑ)
  const handleUsStateChange = (e) => {
    const newState = e.target.value;
    setSelectedUsState(newState);
    const stateObj = usLocations.find((s) => s.state === newState);
    let newCity = "";
    let newZip = "";

    if (stateObj && stateObj.cities && stateObj.cities.length > 0) {
      newCity = stateObj.cities[0].city;
      const firstZips = stateObj.cities[0].zips || [];
      if (firstZips.length > 0) {
        newZip = firstZips[0];
      }
    }

    setSelectedUsCity(newCity);
    handleInputChange("us_state", newState);
    if (newZip) {
      handleInputChange("us_zip", newZip);
    } else {
      handleInputChange("us_zip", "");
    }
  };

  // 🔹 City change handler (ΗΠΑ)
  const handleUsCityChange = (e) => {
    const newCity = e.target.value;
    setSelectedUsCity(newCity);
    const stateObj = usLocations.find((s) => s.state === selectedUsState);
    if (!stateObj || !stateObj.cities) {
      handleInputChange("us_zip", "");
      return;
    }
    const cityObj = stateObj.cities.find((c) => c.city === newCity);
    const firstZip = cityObj?.zips?.[0] || "";
    handleInputChange("us_zip", firstZip);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError("");

      // Ελάχιστες απαιτήσεις backend
      if (!profile.religious_preference_id) {
        setError(
          t("religious_preference_required") ||
          "Please select a religious preference."
        );
        setSaving(false);
        return;
      }
      if (profile.money_available === "" || profile.money_available === null) {
        setError(
          t("money_available_required") ||
          "Please enter how much you can spend on food."
        );
        setSaving(false);
        return;
      }
      if (
        profile.household_members === "" ||
        profile.household_members === null
      ) {
        setError(
          t("household_members_required") ||
          "Please enter household members."
        );
        setSaving(false);
        return;
      }

      // 🔹 ΠΟΙΑ χώρα θα στείλουμε ΤΕΛΙΚΑ στο backend;
      const effectiveCountryId = profile.location_country_id
        ? Number(profile.location_country_id)
        : locCountry
          ? Number(locCountry)
          : null;

      const isUS = String(effectiveCountryId) === "2";

      // 🔹 Validation για US ZIP
      if (isUS && !zipSupported) {
        setError(
          t("unsupported_us_zip") ||
          "Ο συγκεκριμένος ZIP δεν υποστηρίζεται προς το παρόν ή δεν είναι έγκυρος."
        );
        setSaving(false);
        return;
      }

      const trimmedZip = (profile.us_zip || "").trim();

      const normalizedCookingServings =
        profile.cooking_servings !== "" && profile.cooking_servings !== null
          ? Number(profile.cooking_servings)
          : profile.batch_servings !== "" && profile.batch_servings !== null
            ? Number(profile.batch_servings)
            : null;

      const normalizedBatchServings =
        profile.batch_servings !== "" && profile.batch_servings !== null
          ? Number(profile.batch_servings)
          : normalizedCookingServings;

      const payload = {
        ...profile,
        // ✅ Στέλνουμε ΠΑΝΤΑ τη χώρα στο backend
        location_country_id: effectiveCountryId,

        food_types: profile.food_types || [],
        meal_options: profile.meal_options || [],
        diet_type_ids: profile.diet_type_ids || [],
        country_recipes_ids: profile.country_recipes_ids || [],
        religious_preference_id: profile.religious_preference_id || null,

        batch_servings: normalizedBatchServings,

        // ✅ cooking_servings: μετατρέπουμε σε number ή null (το backend προς το παρόν το αγνοεί, δεν πειράζει)
        cooking_servings: normalizedCookingServings,

        // ✅ US fields: μόνο αν είμαστε USA
        us_state: isUS ? (profile.us_state || selectedUsState || null) : null,
        us_zip: isUS ? (trimmedZip || null) : null,

        // ✅ Deprecated στο backend
        store_location_id: null,
      };

      // 🔹 ΠΑΝΤΑ καλούμε ensureGuestSession ΠΡΙΝ το saveProfile
      //    - Αν είσαι guest → δημιουργεί/ενημερώνει guest session + cookie
      //    - Αν είσαι logged-in → δεν πειράζει, ο backend θα σε δει ως user
      await ensureGuestSession();

      if (process.env.NODE_ENV !== "production") {
        console.debug("[household] saveProfile request", {
          endpoint: "/api/household/profile",
          isGuest: !isLoggedIn,
          payload,
        });
      }

      const res = await household.saveProfile(payload);

      if (process.env.NODE_ENV !== "production") {
        console.debug("[household] saveProfile response", res?.data || null);
      }

      if (res?.data?.success) {
        const p = res.data.profile || payload;
        setProfile((prev) => ({ ...prev, ...p }));
        setOpen(true);
      } else {
        throw new Error(res?.data?.error || "Failed to save profile.");
      }
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        err?.message ||
        "Failed to save household profile."
      );
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Container sx={{ mt: 6 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // — Banner τοποθεσίας
  const bannerText = (() => {
    if (!locCountry)
      return t("no_location_selected") || "Δεν έχει επιλεγεί τοποθεσία.";
    if (isUSCtx) {
      const parts = [
        countryObj?.name_el || "ΗΠΑ",
        profile.us_state || null,
        profile.us_zip || null,
      ]
        .filter(Boolean)
        .join(" • ");
      return parts;
    }
    return countryObj?.name_el || t("country_selected");
  })();

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, md: 5 }, mb: 4 }}>
      {/* Banner + αλλαγή τοποθεσίας */}
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          bgcolor: theme.palette.mode === "dark" ? "#1f2a37" : "#f0fdf4",
          color: theme.palette.mode === "dark" ? "#e5f7ee" : "#064e3b",
          border:
            theme.palette.mode === "dark"
              ? "1px solid #274b3b"
              : "1px solid #bbf7d0",
        }}
      >
        <LocationOnIcon />
        <Typography variant="body1" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {bannerText}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setShowLocationGate(true)}
        >
          {t("change_location") || "Αλλαγή τοποθεσίας"}
        </Button>
      </Box>

      {showLocationGate && (
        <LocationGate forceOpen onClose={() => setShowLocationGate(false)} />
      )}

      <Card
        sx={{
          borderRadius: 4,
          boxShadow: 4,
          bgcolor: "background.paper",
          overflow: "visible",
        }}
      >
        <CardHeader
          avatar={
            <SpaRoundedIcon
              sx={{ fontSize: 48, color: theme.palette.primary.main }}
            />
          }
          title={
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {t("household_profile")}
            </Typography>
          }
          subheader={
            <Typography sx={{ color: theme.palette.text.main }}>
              {profile.id
                ? t("edit_your_household_profile")
                : t("create_new_household_profile")}
            </Typography>
          }
          sx={{
            background: theme.palette.primary.dark,
            color: "#fff",
            borderRadius: "24px 24px 0 0",
            pb: 2,
          }}
        />
        <CardContent>
          <Box component="form" sx={{ mt: 1 }} autoComplete="off">
            <Grid container spacing={3}>
              {/* Household Members */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("household_members")}
                  value={profile.household_members || ""}
                  onChange={(e) =>
                    handleInputChange("household_members", e.target.value)
                  }
                  fullWidth
                  type="number"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PeopleAltRoundedIcon color="primary" sx={{ opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText={t("number_of_people_in_your_household")}
                  sx={{ bgcolor: "background.paper", borderRadius: 2 }}
                />
              </Grid>

              {/* Για πόσα άτομα μαγειρεύεις */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("cooking_servings_label") || "Για πόσα άτομα μαγειρεύεις;"}
                  value={profile.cooking_servings || profile.batch_servings || ""}
                  onChange={(e) => {
                    handleInputChange("cooking_servings", e.target.value);
                    handleInputChange("batch_servings", e.target.value);
                  }}
                  fullWidth
                  type="number"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PeopleAltRoundedIcon color="secondary" sx={{ opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    t("cooking_servings_helper") ||
                    "Π.χ. νοικοκυριό 4 ατόμων αλλά μαγειρεύεις για 8."
                  }
                  sx={{ bgcolor: "background.paper", borderRadius: 2 }}
                />
              </Grid>


              {/* Money Available */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("money_available")}
                  value={profile.money_available || ""}
                  onChange={(e) =>
                    handleInputChange("money_available", e.target.value)
                  }
                  fullWidth
                  type="number"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EuroRoundedIcon
                          color="success"
                          sx={{ opacity: 0.8 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  helperText={t("how_much_can_you_spend_on_food")}
                  sx={{ bgcolor: "background.paper", borderRadius: 2 }}
                />
              </Grid>

              {/* Country */}
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={{ bgcolor: "background.paper", borderRadius: 2 }}
                >
                  <InputLabel>
                    <PublicRoundedIcon sx={{ mr: 1, fontSize: 18 }} />
                    {t("location_country")}
                  </InputLabel>
                  <Select
                    value={profile.location_country_id || ""}
                    label={t("location_country")}
                    onChange={(e) =>
                      handleInputChange("location_country_id", e.target.value)
                    }
                    startAdornment={
                      <FlagCircleRoundedIcon
                        color="primary"
                        sx={{ fontSize: 16, ml: 0.5 }}
                      />
                    }
                  >
                    {countryRecipes
                      .filter((loc) => [2, 1].includes(loc.id)) // ΗΠΑ & Ελλάδα
                      .map((loc) => (
                        <MenuItem key={loc.id} value={loc.id}>
                          <FlagCircleRoundedIcon
                            sx={{ fontSize: 18, color: "primary.light", mr: 1 }}
                          />
                          {loc.country_name}
                        </MenuItem>
                      ))}
                  </Select>
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, color: "text.secondary" }}
                  >
                    {t("where_are_you_living")}
                  </Typography>
                </FormControl>
              </Grid>

              {/* === ΗΠΑ: State → City → ZIP === */}
              {isUSForForm && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: "1px dashed",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t("select_us_area") ||
                        "Επιλογή τοποθεσίας στις ΗΠΑ (Πολιτεία, Πόλη, ZIP)"}
                    </Typography>

                    <Grid container spacing={2}>
                      {/* State */}
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>{t("state") || "Πολιτεία"}</InputLabel>
                          <Select
                            label={t("state") || "Πολιτεία"}
                            value={selectedUsState}
                            onChange={handleUsStateChange}
                            disabled={loadingUsLocations || !usStateOptions.length}
                          >
                            {usStateOptions.map((st) => (
                              <MenuItem key={st} value={st}>
                                {st}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* City */}
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>{t("city") || "Πόλη"}</InputLabel>
                          <Select
                            label={t("city") || "Πόλη"}
                            value={selectedUsCity}
                            onChange={handleUsCityChange}
                            disabled={
                              loadingUsLocations ||
                              !selectedUsState ||
                              !usCityOptions.length
                            }
                          >
                            {usCityOptions.map((city) => (
                              <MenuItem key={city} value={city}>
                                {city}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* ZIP */}
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label={t("zip_code") || "ZIP Code"}
                          value={profile.us_zip || ""}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, "").slice(0, 5);
                            handleInputChange("us_zip", v);
                          }}
                          inputProps={{ inputMode: "numeric", pattern: "\\d{5}" }}
                          placeholder={zipsForSelection[0] || "90037"}
                          fullWidth
                          error={isUSForForm && !!profile.us_zip && !zipSupported}
                          helperText={
                            isUSForForm && profile.us_zip && !zipSupported
                              ? t("unsupported_us_zip") ||
                              "Βάλε ένα υποστηριζόμενο ZIP (5 ψηφία) από τη λίστα."
                              : t("postal_code_helper") ||
                              "Πληκτρολόγησε 5ψήφιο ZIP ή επέλεξε ένα από τα παρακάτω."
                          }
                        />
                      </Grid>
                    </Grid>

                    {/* Προτεινόμενα ZIPs (chips) για την επιλεγμένη πόλη */}
                    <Box
                      sx={{
                        mt: 1.5,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      {visibleZips.map((z) => (
                        <Chip
                          key={z}
                          label={z}
                          onClick={() => handleInputChange("us_zip", z)}
                          clickable
                          color={
                            profile.us_zip === z ? "primary" : "default"
                          }
                          variant={
                            profile.us_zip === z ? "filled" : "outlined"
                          }
                          sx={{ fontWeight: 600 }}
                        />
                      ))}
                      {zipsForSelection.length > visibleZips.length && (
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 1,
                            display: "block",
                            color: "text.secondary",
                          }}
                        >
                          {t("and_more_zips") ||
                            `...και άλλα ${zipsForSelection.length - visibleZips.length
                            } ZIPs`}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}

              {/* Diet Types */}
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={{ bgcolor: "background.paper", borderRadius: 2 }}
                >
                  <InputLabel>
                    <RestaurantMenuRoundedIcon sx={{ mr: 1, fontSize: 18 }} />
                    {t("diet_type")}
                  </InputLabel>
                  <Select
                    multiple
                    open={dietTypeSelectOpen}
                    onOpen={() => setDietTypeSelectOpen(true)}
                    onClose={() => setDietTypeSelectOpen(false)}
                    value={profile.diet_type_ids || []}
                    label={t("diet_type")}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.includes("all")) {
                        if (
                          (profile.diet_type_ids || []).length ===
                          dietTypes.length
                        ) {
                          handleInputChange("diet_type_ids", []);
                        } else {
                          handleInputChange(
                            "diet_type_ids",
                            dietTypes.map((d) => d.id)
                          );
                        }
                      } else {
                        handleInputChange("diet_type_ids", value);
                      }
                    }}
                    renderValue={(selected) =>
                      selected.length === 0
                        ? t("select_diet_type")
                        : selected.length === dietTypes.length
                          ? t("all_diet_types")
                          : dietTypes
                            .filter((d) => selected.includes(d.id))
                            .map((d) => d.diet_name)
                            .join(", ")
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 350,
                          background: "#fff",
                          color: "#24292f",
                        },
                      },
                    }}
                  >
                    <MenuItem value="all">
                      <Checkbox
                        checked={
                          (profile.diet_type_ids || []).length ===
                          dietTypes.length
                        }
                        indeterminate={
                          (profile.diet_type_ids || []).length > 0 &&
                          (profile.diet_type_ids || []).length <
                          dietTypes.length
                        }
                      />
                      <ListItemText primary={t("all_diet_types")} />
                    </MenuItem>
                    {dietTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        <Checkbox
                          checked={
                            (profile.diet_type_ids || []).includes(type.id)
                          }
                        />
                        <ListItemText primary={type.diet_name} />
                      </MenuItem>
                    ))}
                    <Box sx={{ textAlign: "right", px: 2, pb: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setDietTypeSelectOpen(false)}
                        size="small"
                        sx={{ mt: 1 }}
                      >
                        OK
                      </Button>
                    </Box>
                  </Select>
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, color: "text.secondary" }}
                  >
                    {t("choose_your_diet_types")}
                  </Typography>
                </FormControl>
              </Grid>

              {/* Country Recipes */}
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={{ bgcolor: "background.paper", borderRadius: 2 }}
                >
                  <InputLabel>
                    <FlagCircleRoundedIcon sx={{ mr: 1, fontSize: 18 }} />
                    {t("country_recipes")}
                  </InputLabel>
                  <Select
                    multiple
                    open={countrySelectOpen}
                    onOpen={() => setCountrySelectOpen(true)}
                    onClose={() => setCountrySelectOpen(false)}
                    value={profile.country_recipes_ids || []}
                    label={t("country_recipes")}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.includes("all")) {
                        if (
                          (profile.country_recipes_ids || []).length ===
                          countryRecipes.length
                        ) {
                          handleInputChange("country_recipes_ids", []);
                        } else {
                          handleInputChange(
                            "country_recipes_ids",
                            countryRecipes.map((c) => c.id)
                          );
                        }
                      } else {
                        handleInputChange("country_recipes_ids", value);
                      }
                    }}
                    renderValue={(selected) =>
                      selected.length === 0
                        ? t("select_country")
                        : selected.length === countryRecipes.length
                          ? t("all_countries")
                          : countryRecipes
                            .filter((c) => selected.includes(c.id))
                            .map((c) => c.country_name)
                            .join(", ")
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 350,
                          background: "#fff",
                          color: "#24292f",
                        },
                      },
                    }}
                  >
                    <MenuItem value="all">
                      <Checkbox
                        checked={
                          (profile.country_recipes_ids || []).length ===
                          countryRecipes.length
                        }
                        indeterminate={
                          (profile.country_recipes_ids || []).length > 0 &&
                          (profile.country_recipes_ids || []).length <
                          countryRecipes.length
                        }
                      />
                      <ListItemText primary={t("all_countries")} />
                    </MenuItem>
                    {countryRecipes.map((recipe) => (
                      <MenuItem key={recipe.id} value={recipe.id}>
                        <Checkbox
                          checked={
                            (profile.country_recipes_ids || []).includes(
                              recipe.id
                            )
                          }
                        />
                        <ListItemText primary={recipe.country_name} />
                      </MenuItem>
                    ))}
                    <Box sx={{ textAlign: "right", px: 2, pb: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setCountrySelectOpen(false)}
                        size="small"
                        sx={{ mt: 1 }}
                      >
                        OK
                      </Button>
                    </Box>
                  </Select>
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, color: "text.secondary" }}
                  >
                    {t("which_country_cuisines")}
                  </Typography>
                </FormControl>
              </Grid>

              {/* Religious Preferences */}
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={{ bgcolor: "background.paper", borderRadius: 2 }}
                >
                  <InputLabel>
                    <SpaRoundedIcon sx={{ mr: 1, fontSize: 18 }} />
                    {t("religious_preference")}
                  </InputLabel>
                  <Select
                    value={profile.religious_preference_id ?? ""}
                    label={t("religious_preference")}
                    onChange={(e) =>
                      handleInputChange(
                        "religious_preference_id",
                        Number(e.target.value)
                      )
                    }
                  >
                    {religiousPreferences.map((pref) => (
                      <MenuItem key={pref.id} value={pref.id}>
                        {pref.preference_name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, color: "text.secondary" }}
                  >
                    {t("religion_note")}
                  </Typography>
                </FormControl>
              </Grid>

              {/* Food Types */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <RestaurantMenuRoundedIcon
                    sx={{
                      mb: "-4px",
                      mr: 1,
                      color: theme.palette.primary.main,
                      fontSize: 23,
                    }}
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t("food_types")}
                  </Typography>
                  <Tooltip title={t("select_what_you_prefer_to_eat")}>
                    <InfoOutlinedIcon
                      color="info"
                      sx={{
                        ml: 1,
                        fontSize: 17,
                        cursor: "pointer",
                        opacity: 0.8,
                      }}
                    />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    p: 1,
                  }}
                >
                  {foodTypes.map((type) => (
                    <Chip
                      key={type.id}
                      label={type.type_name}
                      icon={
                        <RestaurantMenuRoundedIcon
                          sx={{ fontSize: 16 }}
                        />
                      }
                      color={
                        profile.food_types?.includes(type.id)
                          ? "primary"
                          : "default"
                      }
                      onClick={() => handleFoodTypeCheckboxChange(type.id)}
                      variant={
                        profile.food_types?.includes(type.id)
                          ? "filled"
                          : "outlined"
                      }
                      sx={{
                        fontWeight: 500,
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                        boxShadow: profile.food_types?.includes(type.id)
                          ? 2
                          : 0,
                      }}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Meal Options */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <DinnerDiningRoundedIcon
                    sx={{
                      mb: "-4px",
                      mr: 1,
                      color: theme.palette.secondary.main,
                      fontSize: 23,
                    }}
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t("meal_options")}
                  </Typography>
                  <Tooltip title={t("choose_meal_types")}>
                    <InfoOutlinedIcon
                      color="info"
                      sx={{
                        ml: 1,
                        fontSize: 17,
                        cursor: "pointer",
                        opacity: 0.8,
                      }}
                    />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    p: 1,
                  }}
                >
                  {mealOptions.map((meal) => (
                    <Chip
                      key={meal.id}
                      label={meal.name}
                      icon={
                        <DinnerDiningRoundedIcon
                          sx={{ fontSize: 16 }}
                        />
                      }
                      color={
                        profile.meal_options?.includes(meal.id)
                          ? "secondary"
                          : "default"
                      }
                      onClick={() => handleMealOptionChange(meal.id)}
                      variant={
                        profile.meal_options?.includes(meal.id)
                          ? "filled"
                          : "outlined"
                      }
                      sx={{
                        fontWeight: 500,
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                        boxShadow: profile.meal_options?.includes(meal.id)
                          ? 2
                          : 0,
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={
                  saving ||
                  !authLoaded ||
                  (isUSForForm && !zipSupported) ||
                  (isUSForForm && loadingUsLocations)
                }
                sx={{
                  borderRadius: 3,
                  fontWeight: 700,
                  px: 4,
                  boxShadow: 2,
                  minWidth: 180,
                }}
                onClick={handleSubmit}
              >
                {isLoggedIn
                  ? profile.id
                    ? t("update_profile") || "Update Profile"
                    : t("create_profile") || "Create Profile"
                  : t("update_profile") || "Save Preferences"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth={isMobile ? "xs" : "sm"}
        PaperProps={{
          sx: { borderRadius: 3, p: { xs: 1.5, sm: 3 } },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontSize: { xs: 19, sm: 21 },
            fontWeight: 700,
            pb: 0.5,
          }}
        >
          {t("profile_saved_dialog_title") || "Profile Saved"}
        </DialogTitle>
        <DialogContent
          sx={{
            textAlign: "center",
            fontSize: { xs: 16, sm: 17 },
            mb: 1,
          }}
        >
          <Typography sx={{ mt: 1, mb: 2 }}>
            {t("profile_saved_dialog_message") ||
              "Your household profile was saved successfully!"}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "center",
            pb: { xs: 2, sm: 3 },
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 2 : 1,
          }}
        >
          <Button
            onClick={() => navigate(`/${lang}/client/dashboard`)}
            color="primary"
            variant="contained"
            fullWidth={isMobile}
            sx={{ fontWeight: 700, py: 1.2, fontSize: { xs: 16, sm: 15 } }}
          >
            {t("go_to_dashboard") || "Go to Dashboard"}
          </Button>
          <Button
            onClick={() => setOpen(false)}
            color="secondary"
            fullWidth={isMobile}
            sx={{ fontWeight: 700, py: 1.2, fontSize: { xs: 16, sm: 15 } }}
          >
            {t("continue_editing") || "Continue Editing"}
          </Button>
          <Button
            onClick={() => navigate(`/${lang}/client/meal/plan`)}
            color="primary"
            variant="outlined"
            fullWidth={isMobile}
            sx={{ fontWeight: 700, py: 1.2, fontSize: { xs: 16, sm: 15 } }}
          >
            {t("plan_meals") || "Plan Meals"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HouseholdProfile;
